import { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiCheckCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { mixed, object, string } from "yup";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import Select from "components/Select";
import Textarea from "components/Textarea";
import { showErrorMessage } from "helpers/toastMessage";
import type { IOption } from "models/Form";
import { postSupportTicketAPI } from "services/api/userSetting";
import { RootState } from "store";
import { collectDeviceContext, DeviceContext, formatDeviceContext } from "./deviceContext";

export type CrashReportSource = "crash" | "error" | "manual";

const CATEGORY_OPTIONS: IOption[] = [
  { id: 1, label: "Technical problem", value: "technical" },
  { id: 2, label: "Payment or money concern", value: "payment" },
  { id: 3, label: "Account or documents", value: "account" },
  { id: 4, label: "Something else", value: "other" }
];

const TECHNICAL_CATEGORY = CATEGORY_OPTIONS[0];

interface ICrashReportForm {
  category: IOption | null;
  description: string;
  name: string;
  email: string;
}

interface IProps {
  isOpen: boolean;
  closeModal: VoidFunction;
  source: CrashReportSource;
  eventId?: string;
  errorMessage?: string;
}

export default function CrashReportModal({
  isOpen,
  closeModal,
  source,
  eventId,
  errorMessage
}: Readonly<IProps>) {
  const account = useSelector((state: RootState) => state.authState.account);
  const userDetails = useSelector((state: RootState) => state.userState.userDetails);
  const [deviceContext, setDeviceContext] = useState<DeviceContext | null>(null);
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);

  const isManual = source === "manual";

  const schema = useMemo(
    () =>
      object({
        category: mixed<IOption>().required("Please choose a category"),
        description: string().trim().required("Please tell us what happened"),
        name: isManual ? string().trim().required("Please enter your name") : string().trim(),
        email: isManual
          ? string().trim().required("Please enter your email").email("Enter a valid email")
          : string().trim().email("Enter a valid email")
      }),
    [isManual]
  );

  const {
    register,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset
  } = useForm<ICrashReportForm>({ resolver: yupResolver(schema) as never });

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setDetailsVisible(false);
      reset({
        category: isManual ? null : TECHNICAL_CATEGORY,
        description: "",
        name: userDetails?.dba || "",
        email: account?.email || ""
      });
      collectDeviceContext().then(setDeviceContext);
    }
  }, [isOpen, isManual, account?.email, userDetails?.dba, reset]);

  const onSubmit = async (values: ICrashReportForm) => {
    const context = deviceContext || (await collectDeviceContext());
    const categoryLabel = values.category?.label || "Something else";
    const categoryValue = values.category?.value || "other";

    if (isManual) {
      try {
        await postSupportTicketAPI({
          name: values.name || "HaulPay User",
          email: values.email || account?.email || "",
          message: [
            `Category: ${categoryLabel}`,
            "",
            values.description,
            "",
            formatDeviceContext(context)
          ].join("\n")
        });
      } catch {
        showErrorMessage("We couldn't send your report. Please try again.");
        return;
      }
    }

    Sentry.captureFeedback(
      {
        name: values.name || undefined,
        email: values.email || undefined,
        message: `[${categoryLabel}] ${values.description}`,
        url: window.location.href,
        source,
        associatedEventId: eventId,
        tags: { report_source: source, category: categoryValue }
      },
      {
        captureContext: { contexts: { device_report: context } },
        attachments: [
          {
            filename: "device-info.json",
            data: JSON.stringify({ ...context, errorMessage: errorMessage || null }, null, 2),
            contentType: "application/json"
          }
        ]
      }
    );

    setSubmitted(true);
  };

  const descriptionLabel = isManual
    ? "Tell us what's going on"
    : "What were you doing when this happened?";
  const thankYouMessage = isManual
    ? "Your report has been sent to our team. We've emailed you a ticket confirmation."
    : "Your report has been sent to our team. We'll look into it right away.";

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      showCloseButton
      size="md"
      dialogClass="!bg-foreground !p-4 !border !border-card gap-4">
      {isSubmitted ? (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <Icon path={mdiCheckCircleOutline} className="w-14 h-14 text-success" />
          <h2 className="text-xl font-semibold text-text">Thank you!</h2>
          <p className="text-subtitle">{thankYouMessage}</p>
          <Button variant="contained" className="w-full" onClick={closeModal}>
            Done
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <h2 className="text-xl font-semibold text-text">Report a Problem</h2>
            <p className="mt-1 text-sm text-subtitle">
              Tell us what happened and our team will look into it.
            </p>
          </div>

          {errorMessage && (
            <div className="p-3 text-xs break-words rounded-lg bg-error/10 text-error">
              {errorMessage}
            </div>
          )}

          <Controller
            name="category"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                label="What's this about?"
                placeholder="Choose a category"
                options={CATEGORY_OPTIONS}
                value={value}
                onChange={onChange}
                error={errors.category as FieldError}
              />
            )}
          />

          <Textarea
            name="description"
            register={register}
            error={errors.description}
            label={descriptionLabel}
            rows={4}
            placeholder="Describe the issue and any steps that led to it..."
          />

          {isManual && (
            <Input name="name" register={register} error={errors.name} label="Your Name" />
          )}

          <Input
            name="email"
            type="email"
            register={register}
            error={errors.email}
            label={isManual ? "Contact Email" : "Contact Email (optional)"}
          />

          <div className="text-xs text-subtitle">
            <button
              type="button"
              className="underline text-primary"
              onClick={() => setDetailsVisible(!isDetailsVisible)}>
              {isDetailsVisible ? "Hide" : "View"} the device info we&apos;ll include
            </button>
            {isDetailsVisible && deviceContext && (
              <div className="p-3 mt-2 overflow-y-auto rounded-lg max-h-40 bg-background">
                {Object.entries(deviceContext).map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span>{key}</span>
                    <span className="text-right break-all">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" className="flex-1" loading={isSubmitting}>
              Send Report
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
